import { useState, useEffect, useRef } from "react";
import { createProduct, createMedia, deleteProduct, updateProduct, deleteMedia } from "../admin/api/adminApi";
import { getCategories, getDetails } from "../api/frontApi";
import { API_BASE_URL } from "../config/api"; 

export const useFormProduct = ({ initialProduct, onSuccess }) => {
  const [product, setProduct] = useState({
    name: "",
    description: "",
    category: "",
    stock: 0,
    price: 0.0,
    medias: [],
  });


  const [previews, setPreviews] = useState({ cover: null, medias: [] });
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [errors, setErrors] = useState({});
  const [isCoverSelected, setIsCoverSelected] = useState(initialProduct ?  Boolean(initialProduct.medias.some(el => el.isCoverImage)) : false);
  const [showMediaModal, setShowMediaModal] = useState(false);

  const fileInputRef = useRef(null);

  const titleText = initialProduct
    ? "Modifier un Produit"
    : "Ajouter un Produit";

  /* =========================================
     LOAD CATEGORIES
  ========================================= */

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data);
      } catch (e) {
        console.error("Erreur chargement catégories", e);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  /* =========================================
     LOAD PRODUCT (EDIT MODE)
  ========================================= */

  useEffect(() => {
    if (!initialProduct) return;

    const loadProductDetails = async () => {
      try {
        const response = await getDetails(initialProduct.id);

        const formattedProduct = {
          ...initialProduct,
          description: response.description,
          medias: response.medias,
          category: initialProduct.category.id,
        };

        const imagePreviews = response.medias
          .filter(m => m.type === "image")
          .map(m => `${API_BASE_URL}/${m.path}`);

        const coverMedia = response.medias.find(m => m.isCoverImage);

        setPreviews({
          cover: coverMedia ? `${API_BASE_URL}/${coverMedia.path}` : null,
          medias: imagePreviews,
        });

        if (coverMedia) setIsCoverSelected(true);

        setProduct(formattedProduct);

      } catch (error) {
        console.error("Erreur récupération produit:", error);
      }
    };

    loadProductDetails();
  }, [initialProduct]);

  /* =========================================
     INPUT HANDLERS
  ========================================= */

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsedValue =
      name === "price" ? parseFloat(value) || 0 : value;

    setProduct(prev => ({ ...prev, [name]: parsedValue }));
  };

  const handleCoverImageChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    handleAddMedia({
      type: "image",
      file: selectedFile,
      isCoverImage: true,
    });

    setIsCoverSelected(true);
    setErrors(prev => ({ ...prev, isCoverImage: null }));
  };

  const handleAddMedia = (newMedia) => {

      setProduct(prev => {
      const updatedMedias = newMedia.isCoverImage
      ? prev.medias.map(m => ({ ...m, isCoverImage: false }))
      : prev.medias;

    return {
      ...prev,
      medias: [...updatedMedias, { ...newMedia, isCoverImage: newMedia.isCoverImage ?? false }],
    };
  });

    if (newMedia.type === "image" && newMedia.file) {
      setPreviews(prev => ({
        ...prev,
        medias: [...prev.medias, URL.createObjectURL(newMedia.file)],
      }));
    }
  };

  const handleRemoveMedia = async (index) => {

  const media = product.medias[index];
  if (!media) return;

  const confirmed = window.confirm(
    "Voulez-vous vraiment supprimer ce média ?"
  );

  if (!confirmed) return;

 
    if (media.id) {
      await deleteMedia(media.id);
    }
    setProduct(prev => {
      const updated = [...prev.medias];
      const removed = updated[index];

      if (removed?.isCoverImage) {
        setIsCoverSelected(false);
        setPreviews(p => ({ ...p, cover: null }));
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }

      updated.splice(index, 1);
      return { ...prev, medias: updated };
    });

    setPreviews(prev => {
      const updated = [...prev.medias];
      updated.splice(index, 1);
      return { ...prev, medias: updated };
    });
  };

  /* =========================================
     VALIDATION
  ========================================= */

  const validateForm = () => {
    const newErrors = {};

    if (!product.name) newErrors.name = "Le nom est obligatoire";
    if (!product.category) newErrors.category = "La catégorie est obligatoire";
    if (!isCoverSelected)
      newErrors.coverImage = "L'image de couverture est obligatoire";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  function diffMedias(initialMedias = [], currentMedias = []) {

  const mediasToDelete = initialMedias.filter(
    initial =>
      !currentMedias.some(current => current.id === initial.id)
  );

  const mediasToCreate = currentMedias.filter(
    current => !current.id
  );
  const mediasToKeep = currentMedias.filter(
    current => current.id
  );
  return {
    mediasToDelete,
    mediasToCreate,
    mediasToKeep,
  };
}
  /* =========================================
     SUBMIT
  ========================================= */

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  try {
    const transformedProduct = {
    ...product,
    category: product.category,
    media: []
};
    const formData = buildProductFormData(transformedProduct);

    let productId;

    if (initialProduct) {
      const response = await updateProduct(
        product,
      );
      productId = initialProduct.id;

      await syncMedias(productId, initialProduct.medias);
    } else {
      const response = await createProduct(formData);

      if (response.status !== "success") {
        throw new Error("Product creation failed");
      }

      productId = response.product.id;

      await syncMedias(productId, []);
    }

    resetForm();
    onSuccess?.();

  } catch (error) {
    console.error("Erreur:", error);
  }
  };

  function buildProductFormData(product) {
        const formData = new FormData();
        formData.append("nom", product.name);
        formData.append("description", product.description);
        formData.append("categorie", product.category);
        formData.append("stock", product.stock);
        formData.append("price", product.price);
        return formData;
    }

  function buildMediaFormData(media) {
      const mediaFormData = new FormData();
      mediaFormData.append("type", media.type);

      if (media.type === "image") {
          mediaFormData.append("file", media.file);
          mediaFormData.append("isCover", media.isCoverImage ?? false);
      } else {
          mediaFormData.append("link", media.link);
      }

      return mediaFormData;
  }

  const syncMedias = async (productId, initialMedias = []) => {
      const { mediasToDelete, mediasToCreate } = diffMedias(
          initialMedias,
          product.medias
      );

      const deletePromises = mediasToDelete.map(media =>
          deleteMedia(media.id)
      );

      const createPromises = mediasToCreate.map(media =>
          createMedia(buildMediaFormData(media), productId)
      );

      await Promise.all([...deletePromises, ...createPromises]);
  };

  const updateProductAndMedias = async () =>{
    const formData = buildProductFormData(product);

    const productResponse = await updateProduct(
        initialProduct.id,
        formData
    );

    const { mediasToDelete, mediasToCreate } = diffMedias(
        initialProduct.medias,
        product.medias
    );

    // DELETE MEDIA
    const deletePromises = mediasToDelete.map(media =>
        deleteMedia(media.id)

    );



    // CREATE MEDIA
    const createPromises = mediasToCreate.map(media => {
        const mediaFormData = new FormData();
        mediaFormData.append("type", media.type);

        if (media.type === "image") {
        mediaFormData.append("file", media.file);
        mediaFormData.append("isCover", media.isCoverImage ?? false);
        } else {
        mediaFormData.append("link", media.link);
        }

        return createMedia(mediaFormData, initialProduct.id);
    });

    await Promise.all([...deletePromises, ...createPromises]);
  }
  const resetForm = () => {
    setProduct({
      name: "",
      description: "",
      category: "",
      stock: 0,
      price: 0.0,
      medias: [],
    });

    setPreviews({ cover: null, medias: [] });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setIsCoverSelected(false);
  };

  const removeProduct = async () => {
    if (!initialProduct) return;
    try {
      await deleteProduct(initialProduct.id);
      onSuccess?.();
    } catch (error) {
      console.error("Erreur suppression:", error);
    }
  };

  return {
    product,
    previews,
    categories,
    loadingCategories,
    errors,
    showMediaModal,
    setShowMediaModal,
    fileInputRef,
    titleText,
    handleChange,
    handleCoverImageChange,
    handleAddMedia,
    handleRemoveMedia,
    handleSubmit,
    removeProduct,
  };
};